import {
  ScheduledBankPaymentGroupStateEnum,
  TScheduledBankPaymentGroupState
} from '@/features/payments/types/BatchPayment';
import {
  PAYMENT_GROUPS,
  PaymentGroup,
  PaymentGroupEnum,
  TPaymentListItem
} from '../types/ManagePayments';
import { TPaymentFetchContext } from '../types/PaymentKindStrategy';
import { PAYMENT_DATA_SOURCES } from '../data/paymentDataSources';

// Which backend states populate each tab — mirrors the product's Scheduled / Action required / Batch history split.
export const STATES_BY_GROUP: Record<PaymentGroup, TScheduledBankPaymentGroupState[]> = {
  [PaymentGroupEnum.SCHEDULED]: [
    ScheduledBankPaymentGroupStateEnum.APPROVED,
    ScheduledBankPaymentGroupStateEnum.IN_PROGRESS
  ],
  [PaymentGroupEnum.ACTION_REQUIRED]: [
    ScheduledBankPaymentGroupStateEnum.PENDING_APPROVAL,
    ScheduledBankPaymentGroupStateEnum.COMPLETED_WITH_FAILURES,
    ScheduledBankPaymentGroupStateEnum.FAILED
  ],
  [PaymentGroupEnum.BATCH_HISTORY]: [
    ScheduledBankPaymentGroupStateEnum.COMPLETED,
    ScheduledBankPaymentGroupStateEnum.REJECTED,
    ScheduledBankPaymentGroupStateEnum.EXPIRED
  ]
};

// Inverse of STATES_BY_GROUP — since that mapping is exhaustive and disjoint across all 8
// backend states, this lets us fetch everything once and bucket client-side instead of making
// one filtered request per tab.
export const GROUP_BY_STATE = PAYMENT_GROUPS.reduce(
  (acc, group) => {
    STATES_BY_GROUP[group].forEach((state) => {
      acc[state] = group;
    });
    return acc;
  },
  {} as Record<TScheduledBankPaymentGroupState, PaymentGroup>
);

export const EMPTY_PAYMENTS_BY_GROUP: Record<PaymentGroup, TPaymentListItem[]> = {
  [PaymentGroupEnum.SCHEDULED]: [],
  [PaymentGroupEnum.ACTION_REQUIRED]: [],
  [PaymentGroupEnum.BATCH_HISTORY]: []
};

// Re-exported under its previous name for anything still importing it from here - the shape is
// now owned by PaymentKindStrategy.ts since every data source needs it too, not just this fetch.
export type TFetchPaymentsByGroupParams = TPaymentFetchContext;

/**
 * Shared fetch-and-bucket pipeline behind both the Manage Payments page (useManagePayment) and
 * the nav's approval-required count badge (useApprovalRequiredCount) - every source registered in
 * PAYMENT_DATA_SOURCES (see data/paymentDataSources.ts) is fetched independently
 * (`Promise.allSettled`, so one failing doesn't blank out the others) and merged before bucketing,
 * so "what counts as needing approval" can't drift out of sync between the page and the badge.
 * Adding a new source only means adding an entry there - this pipeline itself doesn't know or care
 * how many sources there are, or what kind of row each one produces.
 *
 * Manage Payment covers scheduled payments AND true batch groups, but only the latter is a
 * batch-specific capability, so a business without batch payments enabled (or without batch
 * *permission* - see hasBatchPaymentAccess) still gets a working scheduled-payments count/list.
 */
export const fetchPaymentsByGroup = async (
  ctx: TPaymentFetchContext
): Promise<Record<PaymentGroup, TPaymentListItem[]>> => {
  const results = await Promise.allSettled(
    PAYMENT_DATA_SOURCES.map((source) => {
      const isEnabled = source.isEnabled ? source.isEnabled(ctx) : true;
      return isEnabled ? source.fetch(ctx) : Promise.resolve([]);
    })
  );

  const items = results.flatMap((result) => (result.status === 'fulfilled' ? result.value : []));

  const grouped: Record<PaymentGroup, TPaymentListItem[]> = {
    [PaymentGroupEnum.SCHEDULED]: [],
    [PaymentGroupEnum.ACTION_REQUIRED]: [],
    [PaymentGroupEnum.BATCH_HISTORY]: []
  };
  items.forEach((item) => {
    // Force-completed always lands in Batch History, regardless of the underlying state (which
    // can stay FAILED/COMPLETED_WITH_FAILURES even once force-completed).
    const group = item.forceCompleted ? PaymentGroupEnum.BATCH_HISTORY : GROUP_BY_STATE[item.state];
    if (!group) return;
    grouped[group].push(item);
  });
  return grouped;
};
